// Editing a payload without editing JSON.
//
// Raw JSON is honest and it is the wrong tool for almost everyone. To
// change which zone was correct you had to know the box labelled
// "Centre-Back" is spelled centre-back, type it inside an array, and keep
// the commas valid — to say something you could have said by pointing.
// Worse, nothing told you which fields a kind even takes.
//
// So every kind gets a real form. Spatial answers are picked on the pitch
// itself, using the SAME component the preview draws with, so what an
// author clicks is literally what a learner will tap. Lists of options,
// steps and pairs are add/remove rows rather than hand-balanced arrays.
//
// Raw JSON stays one click away for every kind. A form can only edit the
// fields someone thought of; the payload itself must always be editable.
import { ArrowDown, ArrowUp, Info, Plus, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MarkingsPitch, ZonePitch } from "./Pitches";
import SceneEditor from "./SceneEditor";
import {
  BASE_ZONES,
  FORMATION_LAYOUTS,
  PITCH_MARKINGS,
  layoutFor,
  markingLabel,
  roleLabel,
  zoneLabel,
} from "./geometry";

type Payload = Record<string, any>;

// Every kind the Academy ships. Anything not listed falls back to raw
// JSON rather than to a form that would guess at its shape.
export const FORM_KINDS = new Set([
  "notice_prompt",
  "explanation",
  "reason_prompt",
  "match_mission",
  "reflection_prompt",
  "diagram",
  "formation_board",
  "pitch_markings",
  "assessment_mcq",
  "assessment_multi_select",
  "assessment_pitch_zone",
  "assessment_pitch_marking",
  "assessment_formation",
  "assessment_trace_pass",
  "assessment_board_place",
  "assessment_match_pairs",
  "assessment_sort_buckets",
  "assessment_order_steps",
  "play_scene",
  "assessment_scene",
]);

export default function PayloadForm({
  kind,
  payload,
  onChange,
}: {
  kind: string;
  payload: Payload;
  onChange: (next: Payload) => void;
}) {
  const p = payload ?? {};
  const set = (patch: Payload) => onChange({ ...p, ...patch });

  switch (kind) {
    case "notice_prompt":
      return (
        <Form>
          <Text
            label="Framing line"
            help="One sentence, before the question."
            value={p.primeText}
            onChange={(v) => set({ primeText: v })}
          />
          <Text
            label="What to notice"
            rows={3}
            value={p.noticeQuestion}
            onChange={(v) => set({ noticeQuestion: v })}
          />
        </Form>
      );

    case "reason_prompt":
      return (
        <Form>
          <Text
            label="Question"
            value={p.question}
            onChange={(v) => set({ question: v })}
          />
          <Text
            label="The reasoning (revealed after they try)"
            rows={5}
            value={p.revealedReasoning}
            onChange={(v) => set({ revealedReasoning: v })}
          />
        </Form>
      );

    case "match_mission":
      return (
        <Form>
          <Text
            label="The task"
            rows={3}
            value={p.task}
            onChange={(v) => set({ task: v })}
          />
          <Line
            label="Duration hint"
            placeholder="Five minutes, one player"
            value={p.durationHint}
            onChange={(v) => set({ durationHint: v })}
          />
        </Form>
      );

    case "reflection_prompt":
      return (
        <Form>
          <StringList
            label="Prompts"
            help="Questions with no right answer. One per row."
            value={p.prompts ?? []}
            onChange={(v) => set({ prompts: v })}
            placeholder="Did you spot it?"
          />
        </Form>
      );

    case "explanation":
      return <ExplanationForm payload={p} set={set} />;

    case "diagram":
      return (
        <Form>
          <Text
            label="Caption"
            value={p.caption}
            onChange={(v) => set({ caption: v })}
          />
          <ZonePicker
            label="Zones to light up"
            help="Revealed one at a time, in this order."
            value={p.zones ?? []}
            onChange={(v) => set({ zones: v })}
          />
          <ArrowsEditor
            value={p.arrows ?? []}
            onChange={(v) => set({ arrows: v })}
          />
        </Form>
      );

    case "formation_board":
      return (
        <Form>
          <Text
            label="Caption"
            value={p.caption}
            onChange={(v) => set({ caption: v })}
          />
          <FormationPicker
            formation={p.formation}
            team={p.team}
            onChange={(formation, team) => set({ formation, team })}
          />
          <RolePicker
            label="Highlight these players"
            formation={p.formation}
            value={p.highlight ?? []}
            onChange={(v) => set({ highlight: v })}
          />
          <MovesEditor
            formation={p.formation}
            value={p.moves ?? []}
            onChange={(v) => set({ moves: v })}
          />
        </Form>
      );

    case "pitch_markings":
      return (
        <Form>
          <Rows
            label="Steps"
            help="One group of markings per step."
            items={p.steps ?? []}
            onChange={(v) => set({ steps: v })}
            blank={{ caption: "", highlight: [] as string[] }}
            render={(step, update) => (
              <div className="space-y-2">
                <Text
                  label="Caption"
                  value={step.caption}
                  onChange={(v) => update({ ...step, caption: v })}
                />
                <MarkingPicker
                  label="Markings"
                  value={step.highlight ?? []}
                  onChange={(v) => update({ ...step, highlight: v })}
                />
              </div>
            )}
          />
        </Form>
      );

    case "assessment_mcq":
      return (
        <Form>
          <Text
            label="Question"
            value={p.text}
            onChange={(v) => set({ text: v })}
          />
          <OptionsEditor
            value={p.options ?? []}
            onChange={(v) => set({ options: v })}
            correct={p.correctOptionId ? [p.correctOptionId] : []}
            onCorrectChange={(ids) => set({ correctOptionId: ids[0] })}
            single
          />
          <Text
            label="Explanation"
            rows={3}
            value={p.explanation}
            onChange={(v) => set({ explanation: v })}
          />
        </Form>
      );

    case "assessment_multi_select":
      return (
        <Form>
          <Text
            label="Question"
            value={p.text}
            onChange={(v) => set({ text: v })}
          />
          <OptionsEditor
            value={p.options ?? []}
            onChange={(v) => set({ options: v })}
            correct={p.correctOptionIds ?? []}
            onCorrectChange={(ids) => set({ correctOptionIds: ids })}
          />
          {(p.options ?? []).length > 0 &&
            (p.correctOptionIds ?? []).length === (p.options ?? []).length && (
              <Warn>
                Every option is correct, so this cannot be got wrong. Add a
                distractor — the server refuses to publish it like this.
              </Warn>
            )}
          <Text
            label="Explanation"
            rows={3}
            value={p.explanation}
            onChange={(v) => set({ explanation: v })}
          />
        </Form>
      );

    case "assessment_pitch_zone":
      return (
        <Form>
          <Text
            label="Question"
            value={p.text}
            onChange={(v) => set({ text: v })}
          />
          <ZonePicker
            label="Correct zones"
            help="Click a zone to add or remove it. Several can be right — a question about full-backs should accept either side."
            value={p.correctZoneIds ?? []}
            onChange={(v) => set({ correctZoneIds: v })}
          />
          <Text
            label="Explanation"
            rows={3}
            value={p.explanation}
            onChange={(v) => set({ explanation: v })}
          />
        </Form>
      );

    case "assessment_pitch_marking":
      return (
        <Form>
          <Text
            label="Question"
            value={p.text}
            onChange={(v) => set({ text: v })}
          />
          <MarkingPicker
            label="Correct markings"
            help="Markings that mirror each other — both touchlines, both boxes — are separate ids. A question asking for 'the touchline' should list both."
            value={p.correctMarkingIds ?? []}
            onChange={(v) => set({ correctMarkingIds: v })}
          />
          <Text
            label="Explanation"
            rows={3}
            value={p.explanation}
            onChange={(v) => set({ explanation: v })}
          />
        </Form>
      );

    case "assessment_trace_pass":
      return (
        <Form>
          <Text
            label="Question"
            value={p.text}
            onChange={(v) => set({ text: v })}
          />
          <TracePathField
            startZoneId={p.startZoneId}
            path={p.correctPath ?? []}
            onChange={(startZoneId, correctPath) =>
              set({ startZoneId, correctPath })
            }
          />
          <Text
            label="Explanation"
            rows={3}
            value={p.explanation}
            onChange={(v) => set({ explanation: v })}
          />
        </Form>
      );

    case "assessment_formation":
      return (
        <Form>
          <div className="space-y-1.5">
            <Label className="text-xs">Mode</Label>
            <Select
              value={p.mode ?? "move"}
              onValueChange={(v) => set({ mode: v })}
            >
              <SelectTrigger className="cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="move">
                  Move — where should this player go?
                </SelectItem>
                <SelectItem value="spot">
                  Spot — which player is wrong?
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Text
            label="Question"
            value={p.text}
            onChange={(v) => set({ text: v })}
          />
          <FormationPicker
            formation={p.formation}
            team={p.team}
            onChange={(formation, team) => set({ formation, team })}
          />
          {p.mode === "spot" ? (
            <>
              <MovesEditor
                formation={p.formation}
                value={p.moves ?? []}
                onChange={(v) => set({ moves: v })}
              />
              <RolePicker
                label="The player who is wrong"
                formation={p.formation}
                value={p.correctRoles ?? []}
                onChange={(v) => set({ correctRoles: v })}
              />
            </>
          ) : (
            <>
              <RolePicker
                label="The player being asked about"
                formation={p.formation}
                value={p.highlightRole ? [p.highlightRole] : []}
                onChange={(v) => set({ highlightRole: v[v.length - 1] })}
                single
              />
              <ZonePicker
                label="Where he should go"
                value={p.correctZoneIds ?? []}
                onChange={(v) => set({ correctZoneIds: v })}
              />
            </>
          )}
          <Text
            label="Explanation"
            rows={3}
            value={p.explanation}
            onChange={(v) => set({ explanation: v })}
          />
        </Form>
      );

    case "play_scene":
      return (
        <Form>
          <SceneEditor payload={p} onChange={onChange} />
        </Form>
      );

    case "assessment_scene":
      return (
        <Form>
          <Text
            label="Question"
            value={p.text}
            onChange={(v) => set({ text: v })}
          />
          <div className="space-y-1.5">
            <Label className="text-xs">How is it answered?</Label>
            <Select
              value={p.mode ?? "pick_player"}
              onValueChange={(v) => set({ mode: v })}
            >
              <SelectTrigger className="cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pick_player">
                  Tap a player on the pitch
                </SelectItem>
                <SelectItem value="choice">
                  Choose from options underneath
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <SceneEditor payload={p} onChange={onChange} />

          {/* A question scene shows only its LAST frame, so build the
              moment being asked about there. */}
          <Hint>
            The learner sees the last frame only — build the moment
            being asked about in it.
          </Hint>

          {p.mode === "choice" ? (
            <OptionsEditor
              value={p.options ?? []}
              onChange={(v) => set({ options: v })}
              correct={p.correctOptionId ? [p.correctOptionId] : []}
              onCorrectChange={(ids) => set({ correctOptionId: ids[0] })}
              single
            />
          ) : (
            <div className="space-y-2">
              <Label className="text-xs">Who is the right answer?</Label>
              <Hint>
                Every player is tappable, so at least one must be wrong.
              </Hint>
              <div className="flex flex-wrap gap-1">
                {(p.players ?? []).map((pl: any) => {
                  const on = (p.correctPlayerIds ?? []).includes(pl.id);
                  return (
                    <button
                      key={pl.id}
                      type="button"
                      onClick={() =>
                        set({
                          correctPlayerIds: on
                            ? (p.correctPlayerIds ?? []).filter(
                                (x: string) => x !== pl.id
                              )
                            : [...(p.correctPlayerIds ?? []), pl.id],
                        })
                      }
                      className={
                        "cursor-pointer rounded border px-2 py-1 text-[11px] font-medium transition-colors " +
                        (on
                          ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
                          : "text-muted-foreground hover:border-emerald-500/50")
                      }
                    >
                      {pl.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Text
            label="Explanation"
            rows={3}
            value={p.explanation}
            onChange={(v) => set({ explanation: v })}
          />
        </Form>
      );

    case "assessment_board_place":
      return (
        <BoardPlaceForm payload={p} set={set} />
      );

    case "assessment_match_pairs":
      return <MatchPairsForm payload={p} set={set} />;

    case "assessment_sort_buckets":
      return <SortBucketsForm payload={p} set={set} />;

    case "assessment_order_steps":
      return <OrderStepsForm payload={p} set={set} />;

    default:
      return null;
  }
}

// ─── Primitives ─────────────────────────────────────────────────────────

function Form({ children }: { children: React.ReactNode }) {
  return <div className="min-w-0 space-y-4">{children}</div>;
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
      <Info className="mt-0.5 h-3 w-3 shrink-0" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-700">
      {children}
    </p>
  );
}

function Text({
  label,
  help,
  value,
  onChange,
  rows = 2,
  placeholder,
}: {
  label: string;
  help?: string;
  value?: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {help && <Hint>{help}</Hint>}
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full resize-y rounded-md border bg-transparent p-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40"
      />
    </div>
  );
}

function Line({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

// A generic add/remove/reorder list. Every repeated thing in a payload —
// options, steps, moves, prompts — is one of these, so they all behave
// the same way instead of each inventing its own controls.
function Rows<T>({
  label,
  help,
  items,
  onChange,
  blank,
  render,
  reorder = false,
}: {
  label: string;
  help?: string;
  items: T[];
  onChange: (v: T[]) => void;
  blank: T;
  render: (item: T, update: (next: T) => void, index: number) => React.ReactNode;
  reorder?: boolean;
}) {
  const update = (i: number, next: T) =>
    onChange(items.map((it, j) => (j === i ? next : it)));
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div className="min-w-0 space-y-2">
      <Label className="text-xs">{label}</Label>
      {help && <Hint>{help}</Hint>}
      {items.map((item, i) => (
        <div key={i} className="flex min-w-0 gap-2 rounded-lg border p-2.5">
          <span className="w-5 shrink-0 pt-1 text-xs tabular-nums text-muted-foreground">
            {i + 1}
          </span>
          <div className="min-w-0 flex-1">{render(item, (n) => update(i, n), i)}</div>
          <div className="flex shrink-0 flex-col gap-1">
            {reorder && (
              <>
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="cursor-pointer text-muted-foreground disabled:opacity-30"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  className="cursor-pointer text-muted-foreground disabled:opacity-30"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => remove(i)}
              className="cursor-pointer text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, structuredClone(blank)])}
        className="flex cursor-pointer items-center gap-1 rounded border border-dashed px-2.5 py-1.5 text-xs text-muted-foreground hover:border-emerald-500/50 hover:text-foreground"
      >
        <Plus className="h-3.5 w-3.5" />
        Add
      </button>
    </div>
  );
}

function StringList({
  label,
  help,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  help?: string;
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  return (
    <Rows
      label={label}
      help={help}
      items={value}
      onChange={onChange}
      blank={""}
      render={(item, update) => (
        <Input
          value={item}
          onChange={(e) => update(e.target.value)}
          placeholder={placeholder}
        />
      )}
    />
  );
}

function Chips({
  ids,
  labelFor,
  onRemove,
}: {
  ids: string[];
  labelFor: (id: string) => string;
  onRemove: (id: string) => void;
}) {
  if (!ids.length) {
    return (
      <p className="text-[11px] text-amber-600">Nothing selected yet.</p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <span
          key={id}
          className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
        >
          {labelFor(id)}
          <button
            type="button"
            onClick={() => onRemove(id)}
            className="cursor-pointer opacity-60 hover:opacity-100"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

// ─── Pickers ────────────────────────────────────────────────────────────

function ZonePicker({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((z) => z !== id) : [...value, id]);

  return (
    <div className="min-w-0 space-y-2">
      <Label className="text-xs">{label}</Label>
      {help && <Hint>{help}</Hint>}
      <div className="flex flex-wrap items-start gap-4">
        <ZonePitch width={200} highlight={value} onZoneClick={toggle} />
        <div className="min-w-[170px] flex-1 space-y-2">
          <Chips ids={value} labelFor={zoneLabel} onRemove={toggle} />
          <div className="flex flex-wrap gap-1">
            {BASE_ZONES.map((z) => (
              <button
                key={z.id}
                type="button"
                onClick={() => toggle(z.id)}
                className={
                  "cursor-pointer rounded border px-1.5 py-0.5 text-[10px] transition-colors " +
                  (value.includes(z.id)
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
                    : "text-muted-foreground hover:border-emerald-500/50")
                }
              >
                {z.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MarkingPicker({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((m) => m !== id) : [...value, id]);

  return (
    <div className="min-w-0 space-y-2">
      <Label className="text-xs">{label}</Label>
      {help && <Hint>{help}</Hint>}
      <div className="flex flex-wrap items-start gap-4">
        <MarkingsPitch
          width={200}
          highlight={value}
          onMarkingClick={toggle}
          showLabels={false}
        />
        <div className="min-w-[170px] flex-1 space-y-2">
          <Chips ids={value} labelFor={markingLabel} onRemove={toggle} />
          <div className="flex flex-wrap gap-1">
            {PITCH_MARKINGS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => toggle(m.id)}
                className={
                  "cursor-pointer rounded border px-1.5 py-0.5 text-[10px] transition-colors " +
                  (value.includes(m.id)
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
                    : "text-muted-foreground hover:border-emerald-500/50")
                }
              >
                {m.id}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FormationPicker({
  formation,
  team,
  onChange,
}: {
  formation?: string;
  team?: string;
  onChange: (formation: string, team: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label className="text-xs">Formation</Label>
        <Select
          value={formation ?? ""}
          onValueChange={(v) => onChange(v, team ?? "blue")}
        >
          <SelectTrigger className="cursor-pointer">
            <SelectValue placeholder="Choose a formation" />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(FORMATION_LAYOUTS).map((f) => (
              <SelectItem key={f} value={f}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Team colour</Label>
        <Select
          value={team ?? "blue"}
          onValueChange={(v) => onChange(formation ?? "4-3-3", v)}
        >
          <SelectTrigger className="cursor-pointer">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="blue">Blue</SelectItem>
            <SelectItem value="red">Red</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function RolePicker({
  label,
  help,
  formation,
  value,
  onChange,
  single = false,
}: {
  label: string;
  help?: string;
  formation?: string;
  value: string[];
  onChange: (v: string[]) => void;
  single?: boolean;
}) {
  const roles = Object.keys(layoutFor(formation ?? ""));

  const toggle = (r: string) => {
    if (single) return onChange(value.includes(r) ? [] : [r]);
    onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  };

  if (!roles.length) {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs">{label}</Label>
        <Warn>Choose a formation first — its roles come from that.</Warn>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-xs">{label}</Label>
      {help && <Hint>{help}</Hint>}
      <div className="flex flex-wrap gap-1">
        {roles.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => toggle(r)}
            title={roleLabel(r)}
            className={
              "cursor-pointer rounded border px-2 py-1 text-[11px] font-medium transition-colors " +
              (value.includes(r)
                ? "border-emerald-500 bg-emerald-500/10 text-emerald-700"
                : "text-muted-foreground hover:border-emerald-500/50")
            }
          >
            {r}
          </button>
        ))}
      </div>
      {value.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {value.map(roleLabel).join(", ")}
        </p>
      )}
    </div>
  );
}

function ZoneSelect({
  value,
  onChange,
  placeholder = "Choose a zone",
}: {
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Select value={value ?? ""} onValueChange={onChange}>
      <SelectTrigger className="cursor-pointer">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {BASE_ZONES.map((z) => (
          <SelectItem key={z.id} value={z.id}>
            {z.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── Per-kind editors that need their own shape ─────────────────────────

function ArrowsEditor({
  value,
  onChange,
}: {
  value: any[];
  onChange: (v: any[]) => void;
}) {
  return (
    <Rows
      label="Arrows"
      help="Drawn between zone centres, after the zones have lit up."
      items={value}
      onChange={onChange}
      blank={{ from: "", to: "", label: "", type: "move" }}
      render={(a, update) => (
        <div className="grid gap-2 sm:grid-cols-2">
          <ZoneSelect value={a.from} onChange={(v) => update({ ...a, from: v })} placeholder="From" />
          <ZoneSelect value={a.to} onChange={(v) => update({ ...a, to: v })} placeholder="To" />
          <Input
            className="sm:col-span-2"
            value={a.label ?? ""}
            onChange={(e) => update({ ...a, label: e.target.value })}
            placeholder="What this movement is called"
          />
        </div>
      )}
    />
  );
}

function MovesEditor({
  formation,
  value,
  onChange,
}: {
  formation?: string;
  value: any[];
  onChange: (v: any[]) => void;
}) {
  const roles = Object.keys(layoutFor(formation ?? ""));
  return (
    <Rows
      label="Movements"
      help="Each one sends a player to a zone, one beat at a time."
      items={value}
      onChange={onChange}
      blank={{ role: "", to: "", label: "", tag: "" }}
      render={(m, update) => (
        <div className="grid gap-2 sm:grid-cols-2">
          <Select value={m.role ?? ""} onValueChange={(v) => update({ ...m, role: v })}>
            <SelectTrigger className="cursor-pointer">
              <SelectValue placeholder="Player" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((r) => (
                <SelectItem key={r} value={r}>
                  {roleLabel(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ZoneSelect value={m.to} onChange={(v) => update({ ...m, to: v })} placeholder="Moves to" />
          <Input
            className="sm:col-span-2"
            value={m.label ?? ""}
            onChange={(e) => update({ ...m, label: e.target.value })}
            placeholder="Verb phrase: 'steps inside into midfield'"
          />
          <Input
            className="sm:col-span-2"
            value={m.tag ?? ""}
            onChange={(e) => update({ ...m, tag: e.target.value })}
            placeholder="Short tag for the on-pitch note: 'inverts inside'"
          />
        </div>
      )}
    />
  );
}

// Ids are generated rather than typed. They are internal plumbing that
// the answer key references — an author editing them by hand can only
// break the link between an option and the key that points at it.
function nextId(prefix: string, existing: { id: string }[]): string {
  let n = existing.length + 1;
  const taken = new Set(existing.map((e) => e.id));
  while (taken.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

function OptionsEditor({
  value,
  onChange,
  correct,
  onCorrectChange,
  single = false,
}: {
  value: any[];
  onChange: (v: any[]) => void;
  correct: string[];
  onCorrectChange: (ids: string[]) => void;
  single?: boolean;
}) {
  const toggle = (id: string) => {
    if (single) return onCorrectChange([id]);
    onCorrectChange(
      correct.includes(id) ? correct.filter((c) => c !== id) : [...correct, id]
    );
  };

  return (
    <div className="min-w-0 space-y-2">
      <Label className="text-xs">Options</Label>
      <Hint>
        {single
          ? "Click the circle to mark the right answer."
          : "Tick every option that applies. At least one must be wrong."}
      </Hint>
      {value.map((o, i) => (
        <div key={o.id ?? i} className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={() => toggle(o.id)}
            title="Mark correct"
            className={
              "h-5 w-5 shrink-0 cursor-pointer border-2 transition-colors " +
              (single ? "rounded-full " : "rounded ") +
              (correct.includes(o.id)
                ? "border-emerald-500 bg-emerald-500"
                : "border-muted-foreground/40")
            }
          />
          <Input
            value={o.text ?? ""}
            onChange={(e) =>
              onChange(
                value.map((x, j) => (j === i ? { ...x, text: e.target.value } : x))
              )
            }
            placeholder="Option text"
          />
          <button
            type="button"
            onClick={() => {
              onChange(value.filter((_, j) => j !== i));
              onCorrectChange(correct.filter((c) => c !== o.id));
            }}
            className="shrink-0 cursor-pointer text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange([...value, { id: nextId("o", value), text: "" }])
        }
        className="flex cursor-pointer items-center gap-1 rounded border border-dashed px-2.5 py-1.5 text-xs text-muted-foreground hover:border-emerald-500/50 hover:text-foreground"
      >
        <Plus className="h-3.5 w-3.5" />
        Add option
      </button>
    </div>
  );
}

function MatchPairsForm({ payload: p, set }: { payload: Payload; set: (x: Payload) => void }) {
  const terms = p.terms ?? [];
  const targets = p.targets ?? [];
  const pairs = p.correctPairs ?? {};

  return (
    <Form>
      <Text label="Question" value={p.text} onChange={(v) => set({ text: v })} />

      <Rows
        label="Meanings to match against"
        items={targets}
        onChange={(v) => set({ targets: v })}
        blank={{ id: "", text: "" }}
        render={(g, update) => (
          <Input
            value={g.text ?? ""}
            onChange={(e) => update({ ...g, text: e.target.value, id: g.id || nextId("g", targets) })}
            placeholder="What one of the terms means"
          />
        )}
      />

      <Rows
        label="Terms"
        help="Each term needs a meaning chosen, or the question cannot be won."
        items={terms}
        onChange={(v) => set({ terms: v })}
        blank={{ id: "", text: "" }}
        render={(t, update) => {
          const id = t.id || nextId("t", terms);
          return (
            <div className="space-y-2">
              <Input
                value={t.text ?? ""}
                onChange={(e) => update({ ...t, text: e.target.value, id })}
                placeholder="The term"
              />
              <Select
                value={pairs[id] ?? ""}
                onValueChange={(v) => set({ correctPairs: { ...pairs, [id]: v } })}
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue placeholder="Its meaning" />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((g: any) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.text || g.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }}
      />

      <Text
        label="Explanation"
        rows={3}
        value={p.explanation}
        onChange={(v) => set({ explanation: v })}
      />
    </Form>
  );
}

function SortBucketsForm({ payload: p, set }: { payload: Payload; set: (x: Payload) => void }) {
  const items = p.items ?? [];
  const buckets = p.buckets ?? [];
  const byItem = p.correctBucketByItem ?? {};

  return (
    <Form>
      <Text label="Question" value={p.text} onChange={(v) => set({ text: v })} />

      <Rows
        label="Groups"
        items={buckets}
        onChange={(v) => set({ buckets: v })}
        blank={{ id: "", text: "" }}
        render={(b, update) => (
          <Input
            value={b.text ?? ""}
            onChange={(e) => update({ ...b, text: e.target.value, id: b.id || nextId("b", buckets) })}
            placeholder="Group name"
          />
        )}
      />

      <Rows
        label="Items"
        help="Every item needs a group, or the question cannot be won."
        items={items}
        onChange={(v) => set({ items: v })}
        blank={{ id: "", text: "" }}
        render={(it, update) => {
          const id = it.id || nextId("i", items);
          return (
            <div className="space-y-2">
              <Input
                value={it.text ?? ""}
                onChange={(e) => update({ ...it, text: e.target.value, id })}
                placeholder="The item"
              />
              <Select
                value={byItem[id] ?? ""}
                onValueChange={(v) =>
                  set({ correctBucketByItem: { ...byItem, [id]: v } })
                }
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue placeholder="Belongs in" />
                </SelectTrigger>
                <SelectContent>
                  {buckets.map((b: any) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.text || b.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }}
      />

      <Text
        label="Explanation"
        rows={3}
        value={p.explanation}
        onChange={(v) => set({ explanation: v })}
      />
    </Form>
  );
}

function OrderStepsForm({ payload: p, set }: { payload: Payload; set: (x: Payload) => void }) {
  const steps = p.steps ?? [];
  // The list is shown and edited in the CORRECT order — that is what an
  // author is deciding. The shuffle that hides it happens on the server.
  const ordered: any[] = (p.correctOrder ?? steps.map((s: any) => s.id))
    .map((id: string) => steps.find((s: any) => s.id === id))
    .filter(Boolean);

  const write = (next: any[]) =>
    set({ steps: next, correctOrder: next.map((s) => s.id) });

  return (
    <Form>
      <Text label="Question" value={p.text} onChange={(v) => set({ text: v })} />
      <Rows
        label="Steps, in the order they happen"
        help="Shown shuffled to the learner. At least three — two is a coin flip, and the server refuses it."
        items={ordered}
        onChange={write}
        reorder
        blank={{ id: "", text: "" }}
        render={(s, update) => (
          <Input
            value={s.text ?? ""}
            onChange={(e) =>
              update({ ...s, text: e.target.value, id: s.id || nextId("s", steps) })
            }
            placeholder="What happens at this point"
          />
        )}
      />
      {ordered.length > 0 && ordered.length < 3 && (
        <Warn>A sequence needs at least three steps.</Warn>
      )}
      <Text
        label="Explanation"
        rows={3}
        value={p.explanation}
        onChange={(v) => set({ explanation: v })}
      />
    </Form>
  );
}

function BoardPlaceForm({ payload: p, set }: { payload: Payload; set: (x: Payload) => void }) {
  const roles: string[] = p.rolesToPlace ?? [];
  const byRole = p.correctZoneByRole ?? {};

  return (
    <Form>
      <Text label="Question" value={p.text} onChange={(v) => set({ text: v })} />
      <FormationPicker
        formation={p.formation}
        team={p.team}
        onChange={(formation, team) => set({ formation, team })}
      />
      <RolePicker
        label="Players to place"
        formation={p.formation}
        value={roles}
        onChange={(v) => set({ rolesToPlace: v })}
      />
      {roles.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs">Where each belongs</Label>
          {roles.map((r) => (
            <div key={r} className="flex items-center gap-2">
              <span className="w-36 shrink-0 text-xs">{roleLabel(r)}</span>
              <ZoneSelect
                value={byRole[r]}
                onChange={(v) => set({ correctZoneByRole: { ...byRole, [r]: v } })}
              />
            </div>
          ))}
        </div>
      )}
      <Text
        label="Explanation"
        rows={3}
        value={p.explanation}
        onChange={(v) => set({ explanation: v })}
      />
    </Form>
  );
}

function ExplanationForm({ payload: p, set }: { payload: Payload; set: (x: Payload) => void }) {
  // These four are a FALLBACK, not part of a lesson that has beats:
  // the app builds beats out of them only when none are authored. The
  // form used to show all four at the top of every explanation, so
  // they got filled in for every lesson and never drawn — 9,103 words
  // of it across 49 lessons. Now they appear only when they would
  // actually be used.
  const hasBeats = ((p.beats ?? []) as unknown[]).length > 0;

  return (
    <Form>
      {hasBeats ? (
        <Hint>
          This lesson has beats, so the four summary fields are not used —
          the beats below are what a learner reads.
        </Hint>
      ) : (
        <>
          <Text label="What it is" rows={3} value={p.whatIsIt} onChange={(v) => set({ whatIsIt: v })} />
          <Text label="Why it matters" rows={3} value={p.whyItMatters} onChange={(v) => set({ whyItMatters: v })} />
          <Text label="How to spot it" rows={3} value={p.howToRecognize} onChange={(v) => set({ howToRecognize: v })} />
          <Text label="How to use it" rows={3} value={p.howToApply} onChange={(v) => set({ howToApply: v })} />
        </>
      )}

      <Rows
        label="Beats"
        help="Leave empty and the four fields above are split into beats automatically. Add beats to control the pacing and attach a visual."
        items={(p.beats ?? []) as any[]}
        onChange={(v) => set({ beats: v })}
        reorder
        blank={{ title: "", text: "" } as any}
        render={(b, update) => (
          <div className="space-y-2">
            <Input
              value={b.title ?? ""}
              onChange={(e) => update({ ...b, title: e.target.value })}
              placeholder="Heading, e.g. 'What it is'"
            />
            <textarea
              value={b.text ?? ""}
              onChange={(e) => update({ ...b, text: e.target.value })}
              rows={3}
              placeholder="The paragraph for this beat."
              className="w-full resize-y rounded-md border bg-transparent p-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
            <BeatVisualEditor
              visual={b.visual}
              onChange={(visual) => {
                const next = { ...b };
                if (visual) next.visual = visual;
                else delete next.visual;
                update(next);
              }}
            />
          </div>
        )}
      />
    </Form>
  );
}

const VISUAL_KINDS = [
  { id: "none", label: "No picture" },
  { id: "compare", label: "Compare — two pitches side by side" },
  { id: "sequence", label: "Sequence — numbered steps" },
  { id: "stat", label: "Stat — one big number" },
  { id: "zones", label: "Zones — the nine-zone map" },
  { id: "board", label: "Board — the tactics board" },
  { id: "markings", label: "Markings — the painted lines" },
];

function BeatVisualEditor({
  visual,
  onChange,
}: {
  visual?: any;
  onChange: (v: any | null) => void;
}) {
  const kind = visual?.kind ?? "none";

  const pick = (k: string) => {
    if (k === "none") return onChange(null);
    // A fresh, valid skeleton per kind, so switching never leaves a
    // half-shaped visual the renderer cannot draw.
    const blanks: Record<string, any> = {
      compare: {
        kind: "compare",
        left: { label: "", zones: [], bandLabel: "", tone: "good", note: "" },
        right: { label: "", zones: [], bandLabel: "", tone: "risk", note: "" },
      },
      sequence: { kind: "sequence", steps: [] },
      stat: { kind: "stat", value: "", label: "", caption: "" },
      zones: { kind: "zones", zones: [] },
      board: { kind: "board", formation: "4-3-3", highlight: [], moves: [] },
      markings: { kind: "markings", highlight: [] },
    };
    onChange(blanks[k]);
  };

  return (
    <div className="space-y-2 rounded border border-dashed p-2">
      <Select value={kind} onValueChange={pick}>
        <SelectTrigger className="cursor-pointer">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {VISUAL_KINDS.map((v) => (
            <SelectItem key={v.id} value={v.id}>
              {v.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {kind === "zones" && (
        <ZonePicker
          label="Zones"
          value={visual.zones ?? []}
          onChange={(v) => onChange({ ...visual, zones: v })}
        />
      )}

      {kind === "markings" && (
        <MarkingPicker
          label="Markings"
          value={visual.highlight ?? []}
          onChange={(v) => onChange({ ...visual, highlight: v })}
        />
      )}

      {kind === "stat" && (
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            value={visual.value ?? ""}
            onChange={(e) => onChange({ ...visual, value: e.target.value })}
            placeholder="70%"
          />
          <Input
            value={visual.label ?? ""}
            onChange={(e) => onChange({ ...visual, label: e.target.value })}
            placeholder="of the ball — and still lose"
          />
          <Input
            className="sm:col-span-2"
            value={visual.caption ?? ""}
            onChange={(e) => onChange({ ...visual, caption: e.target.value })}
            placeholder="Caption underneath"
          />
        </div>
      )}

      {kind === "sequence" && (
        <Rows
          label="Steps"
          items={visual.steps ?? []}
          onChange={(v) => onChange({ ...visual, steps: v })}
          reorder
          blank={{ label: "", text: "" }}
          render={(s, update) => (
            <div className="space-y-1.5">
              <Input
                value={s.label ?? ""}
                onChange={(e) => update({ ...s, label: e.target.value })}
                placeholder="Step heading"
              />
              <Input
                value={s.text ?? ""}
                onChange={(e) => update({ ...s, text: e.target.value })}
                placeholder="What happens"
              />
            </div>
          )}
        />
      )}

      {kind === "board" && (
        <div className="space-y-2">
          <FormationPicker
            formation={visual.formation}
            team={visual.team}
            onChange={(formation, team) => onChange({ ...visual, formation, team })}
          />
          <RolePicker
            label="Highlight"
            formation={visual.formation}
            value={visual.highlight ?? []}
            onChange={(v) => onChange({ ...visual, highlight: v })}
          />
          <MovesEditor
            formation={visual.formation}
            value={visual.moves ?? []}
            onChange={(v) => onChange({ ...visual, moves: v })}
          />
        </div>
      )}

      {kind === "compare" && (
        <div className="space-y-3">
          {(["left", "right"] as const).map((side) => (
            <div key={side} className="space-y-2 rounded border p-2">
              <p className="text-[11px] font-semibold capitalize">{side} pitch</p>
              <Input
                value={visual[side]?.label ?? ""}
                onChange={(e) =>
                  onChange({ ...visual, [side]: { ...visual[side], label: e.target.value } })
                }
                placeholder="Heading above the pitch"
              />
              <ZonePicker
                label="Shaded area"
                value={visual[side]?.zones ?? []}
                onChange={(v) =>
                  onChange({ ...visual, [side]: { ...visual[side], zones: v } })
                }
              />
              <Input
                value={visual[side]?.bandLabel ?? ""}
                onChange={(e) =>
                  onChange({ ...visual, [side]: { ...visual[side], bandLabel: e.target.value } })
                }
                placeholder="Label written on the shaded area — keep it short"
              />
              <Select
                value={visual[side]?.tone ?? "good"}
                onValueChange={(v) =>
                  onChange({ ...visual, [side]: { ...visual[side], tone: v } })
                }
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good — gold</SelectItem>
                  <SelectItem value="risk">Risk — red</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={visual[side]?.note ?? ""}
                onChange={(e) =>
                  onChange({ ...visual, [side]: { ...visual[side], note: e.target.value } })
                }
                placeholder="One line under the pitch"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TracePathField({
  startZoneId,
  path,
  onChange,
}: {
  startZoneId?: string;
  path: string[];
  onChange: (startZoneId: string | undefined, path: string[]) => void;
}) {
  const click = (id: string) => {
    if (!startZoneId) return onChange(id, path);
    const last = path.length ? path[path.length - 1] : startZoneId;
    if (last === id) return; // a pass to where the ball already is
    onChange(startZoneId, [...path, id]);
  };

  return (
    <div className="min-w-0 space-y-2">
      <Label className="text-xs">The route</Label>
      <Hint>
        First click sets where the ball starts; each click after adds a hop.
      </Hint>
      <div className="flex flex-wrap items-start gap-4">
        <ZonePitch
          width={200}
          startZoneId={startZoneId}
          path={path}
          tone="gold"
          onZoneClick={click}
        />
        <div className="min-w-[170px] flex-1 space-y-2">
          <p className="text-xs font-medium">
            {startZoneId ? (
              [startZoneId, ...path].map(zoneLabel).join("  →  ")
            ) : (
              <span className="text-amber-600">
                Click a zone to say where the ball starts.
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!path.length}
              onClick={() => onChange(startZoneId, path.slice(0, -1))}
              className="cursor-pointer rounded border px-2 py-1 text-[11px] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Undo hop
            </button>
            <button
              type="button"
              disabled={!startZoneId}
              onClick={() => onChange(undefined, [])}
              className="cursor-pointer rounded border px-2 py-1 text-[11px] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Clear
            </button>
          </div>
          {startZoneId && path.length < 2 && (
            <Warn>A route needs at least two hops.</Warn>
          )}
        </div>
      </div>
    </div>
  );
}
